import React from 'react'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { client } from '../../../apollo/client'
import { Layout } from '../../../components/layout/layout'
import { REVALIDATION_FREQUENCY } from '../../../constants/revalidation'
import { GlobalProps, withLayoutProps } from '../../../globals/layout'
import { NewsDetail } from '../../../templates/newsDetail'
import { nonNull } from '../../../utils/nonNull'
import { pageQuery } from '../../../utils/pageQuery'
import {
  DetailArticlesFragment,
  FirstPageNewsQuery,
  FirstPageNewsQueryDocument,
  FirstPageNewsQueryVariables,
  NewsDetailQuery,
  NewsDetailQueryDocument,
  NewsDetailQueryVariables,
  RelatedNewsCarouselQuery,
  RelatedNewsCarouselQueryDocument,
  RelatedNewsCarouselQueryVariables,
  RetourQuery,
  RetourQueryDocument,
  RetourQueryVariables,
} from '@gql'

export type CarouselArrayType = DetailArticlesFragment[]

export type NewsDetailPageProps = GlobalProps & {
  data: NewsDetailQuery
  carousel: CarouselArrayType
}

const NewsDetailPage: NextPage<NewsDetailPageProps> = ({ data, carousel, ...layoutProps }) => {
  return (
    <Layout { ...layoutProps }>
      <NewsDetail carousel={carousel} { ...data } />
    </Layout>
  )
}

export default NewsDetailPage

export const getStaticProps: GetStaticProps = async (context) => {
  const uri = `community/news/${context.params?.slug}`

  const retour = await client.query<RetourQuery, RetourQueryVariables>({
    query: RetourQueryDocument,
    variables: {
      uri: `/${uri}`,
    },
  })

  const { retourResolveRedirect } = retour?.data

  if (retourResolveRedirect?.redirectDestUrl !== null && typeof retourResolveRedirect?.redirectDestUrl === 'string') {
    return {
      redirect: {
        destination: retourResolveRedirect?.redirectDestUrl,
        permanent: false,
      },
    }
  }

  const { data } = await pageQuery<NewsDetailQuery, NewsDetailQueryVariables>({
    query: NewsDetailQueryDocument,
    variables: { uri },
  }, context, res => Boolean(res.data.entry))


  if (!data.entry || data.entry.__typename !== 'news_newsEntryType_Entry' || data.entry.status !== 'live') {
    return {
      notFound: true,
    }
  }

  const { newsCategory, newsSubCategory, id } = data.entry

  const categoryId = newsCategory[0]?.id
  const secondaryId = newsSubCategory?.length > 0 ? nonNull(newsSubCategory).map((item: Record<string, unknown>) => item.id) : newsCategory[0]?.id

  const carouselData = await client.query<RelatedNewsCarouselQuery, RelatedNewsCarouselQueryVariables>({
    query: RelatedNewsCarouselQueryDocument,
    variables: { categoryId, secondaryId },
  })

  let newsSlides: typeof carouselData.data.primaryPrimaryCat = []

  if (carouselData?.data?.primaryPrimaryCat?.length && carouselData?.data?.primaryPrimaryCat?.length > 0) {
    newsSlides = newsSlides.concat(carouselData?.data?.primaryPrimaryCat)
  }

  if (carouselData?.data?.primarySecondaryCat) {
    newsSlides = newsSlides.concat(carouselData?.data?.primarySecondaryCat)
  }

  if (carouselData?.data?.secondaryPrimaryCat) {
    newsSlides = newsSlides.concat(carouselData?.data?.secondaryPrimaryCat)
  }

  if (carouselData?.data?.secondarySecondaryCat) {
    newsSlides = newsSlides.concat(carouselData?.data?.secondarySecondaryCat)
  }

  const map = newsSlides.reduce((map, item) => {
    if (item?.__typename === 'news_newsEntryType_Entry' && id !== item.id) {
      map.set(item.id, item)
    }

    return map
  }, new Map())

  const carousel: CarouselArrayType = [ ...map.values() ].slice(0, 6)

  return withLayoutProps({
    path: `/${uri}`,
  }, data.entry ? {
    props: { data, carousel },
    revalidate: REVALIDATION_FREQUENCY,
  } : {
    notFound: true,
  })
}

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await client.query<FirstPageNewsQuery, FirstPageNewsQueryVariables>({
    query: FirstPageNewsQueryDocument,
  })

  return ({
    paths: nonNull(data.entries?.map(entry => entry?.uri ? `/${entry.uri}` : undefined) ?? []),
    fallback: 'blocking',
  })
}